import { useMemo, useRef, useState } from "react";
import { ScrollableMediaGallery } from "./ScrollableMediaGallery";
import {
  AssetSource,
  BrandKitAsset,
  CreateAssetResponse,
  CreateMediaAssetRequest,
  MediaAsset,
} from "pages/media/types";
import UploadFiles, {
  UploadFilesProps,
  UploadFilesRef,
} from "components/uploader/UploadFiles/";
import { GetOneResponse, useTranslate } from "@refinedev/core";
import { Flex, Typography, UploadFile } from "antd";
import { MediaAssetTabs, MediaKind } from "../../../../types";
import { Empty } from "components/Empty/";
import { useTemplateData } from "../../../../hooks/useTemplateData";
import { useAntTheme } from "hooks/useAntTheme/";
import { RecursivePartial } from "components/uploader/types/";
import { DragAndDropArea } from "../../../controls/DragAndDropArea";

export const UploadedMediaAssets = ({
  organizationId,
  assetLibraryId,
  previewWidth,
  previewHeight,
  mediaAsset,
  onReplaceSelectedAsset,
  customAddToCanvas,
  selectionMode,
}: {
  organizationId: string;
  assetLibraryId: string;
  previewWidth: number;
  previewHeight: number;
  mediaAsset: MediaAssetTabs;
  onReplaceSelectedAsset?: (
    assetId: string,
    path: string,
    mediaType: MediaKind
  ) => void;
  customAddToCanvas?: (assetId: string, path: string) => void;
  selectionMode?: boolean;
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const CREATE_ASSET_REQUEST_TYPE = "CreateLibraryAsset";
  const uploadPicturesRef = useRef<UploadFilesRef>(null);
  const uploadVideoRef = useRef<UploadFilesRef>(null);
  const isImagesTab = mediaAsset === MediaAssetTabs.Images;
  const [hasFilesInPreview, setHasFilesInPreview] = useState(false);

  const {
    mediaAssetsData: {
      images,
      videos,
      handleFileAdded,
      updateAllAssets,
      fetchNextPage,
      hasMore,
      isLoadingAssetLibraryAssets,
    },
    brandKitData: {
      logo,
      banner,
      backgroundBrandKitPicture,
      backgroundBrandKitVideo,
      intro,
      transition,
      closing,
    },
  } = useTemplateData();

  const imagesFromBrandkitAndAsset: (Partial<MediaAsset> | BrandKitAsset)[] = [
    logo,
    banner,
    backgroundBrandKitPicture,
  ].filter((asset) => asset !== undefined);

  if (images.length) {
    imagesFromBrandkitAndAsset.push(...images);
  }

  const videosFromBrandkitAndAsset: (Partial<MediaAsset> | BrandKitAsset)[] = [
    backgroundBrandKitVideo,
    intro,
    transition,
    closing,
  ].filter((asset) => asset !== undefined);

  if (videos.length) {
    videosFromBrandkitAndAsset.push(...videos);
  }

  const uploadImageProps: UploadFilesProps = {
    defaultItems: images,
    acceptedMimeTypes: "image/jpeg, image/png, .png, .jpeg, .jpg",
    itemRequestProperties: (file: UploadFile<unknown> | undefined) => ({
      type: CREATE_ASSET_REQUEST_TYPE,
      organization_id: organizationId,
      category: "AssetImage" as const,
      source: AssetSource.UIGenerated,
      description: file?.name,
      asset: {
        type: "Picture" as const,
      },
    }),
    multiple: true,
    hint: <></>,
  };

  const uploadVideoProps: UploadFilesProps = {
    defaultItems: videos,
    acceptedMimeTypes: "video/mp4, .mp4",
    itemRequestProperties: (file: UploadFile<unknown> | undefined) => ({
      type: CREATE_ASSET_REQUEST_TYPE,
      organization_id: organizationId,
      description: file?.name,
      category: "Video" as const,
      source: AssetSource.UIGenerated,
      asset: {
        type: "Video" as const,
      },
    }),
    multiple: true,
    hint: <></>,
  };

  const uploadProps = isImagesTab ? uploadImageProps : uploadVideoProps;
  const uploadRef = isImagesTab ? uploadPicturesRef : uploadVideoRef;
  const currentAssetT = isImagesTab
    ? t("components.templates.settings.imagesVideosTab.images")
    : t("components.templates.settings.imagesVideosTab.videos");

  const assetsToDisplay = useMemo(() => {
    if (isImagesTab) return imagesFromBrandkitAndAsset;
    return videosFromBrandkitAndAsset;
  }, [imagesFromBrandkitAndAsset, isImagesTab, videosFromBrandkitAndAsset]);
  return (
    <Flex
      vertical
      style={{ flex: 1, minHeight: 0, overflow: "hidden" }}
      gap={16}
    >
      <Typography.Text italic type="secondary" style={{ marginTop: 10 }}>
        {t(
          "components.templates.settings.imagesVideosTab.uploadedAssetsDescription",
          { item: currentAssetT }
        )}
      </Typography.Text>
      {assetsToDisplay.length > 0 ? (
        <div
          id="infinite-scroll-images"
          style={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
          }}
        >
          <ScrollableMediaGallery
            scrollableTarget="infinite-scroll-images"
            currentAssetLibraryId={assetLibraryId}
            previewWidth={previewWidth}
            previewHeight={previewHeight}
            assetsFromBrandkitAndLibrary={assetsToDisplay}
            mediaAsset={mediaAsset}
            customAddToCanvas={customAddToCanvas}
            selectionMode={selectionMode}
            onNext={() => {
              fetchNextPage().catch((error) =>
                console.error("Error fetching next page", error)
              );
            }}
            hasMore={hasMore}
            isLoading={isLoadingAssetLibraryAssets}
            onReplaceSelectedAsset={onReplaceSelectedAsset}
          />
        </div>
      ) : (
        <Empty
          style={{ margin: 20 }}
          description={t(
            "components.templates.settings.imagesVideosTab.noAssetsAdded",
            { item: currentAssetT }
          )}
        />
      )}
      <UploadFiles
        {...uploadProps}
        ref={uploadRef}
        showUploadList={false}
        maxFileSize={120}
        onFilesAdded={(files) => {
          setHasFilesInPreview(files.length > 0);
          handleFileAdded(
            assetLibraryId,
            isImagesTab ? uploadPicturesRef : uploadVideoRef
          );
        }}
        onUploadComplete={(files, responses) => {
          // Insert the newly created asset into the list for immediate
          // usage without waiting for the backend to process it
          const itemRequestProperties = uploadProps.itemRequestProperties as (
            file: UploadFile
          ) => RecursivePartial<CreateMediaAssetRequest>;
          const newAssets = responses.map((response, index) => {
            const { asset, category, source, description } =
              itemRequestProperties(files[index]?.originFileObj as UploadFile);

            const newAsset: Partial<MediaAsset> = {
              id: (response as GetOneResponse<CreateAssetResponse>).data
                .asset_id,
              category,
              source,
              description,
              asset_type: asset?.type,
              path: URL.createObjectURL(
                files[index]?.originFileObj ?? new Blob()
              ),
            };

            return newAsset;
          });
          //optimistic update
          updateAllAssets(newAssets);

          uploadVideoRef.current?.reset();
          uploadPicturesRef.current?.reset();
          // wait for UploadFiles to remove the preview display before resetting
          setTimeout(() => {
            setHasFilesInPreview(false);
          }, 500);
        }}
        style={{ borderColor: theme.colorBorder, flex: 1 }}
      >
        <DragAndDropArea inProgress={hasFilesInPreview} />
      </UploadFiles>
    </Flex>
  );
};
